import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SessionService } from 'src/app/feature/home/services/session.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, filter } from 'rxjs';
import { LoadingService } from '../../services/loading.service';
import { UnsubscribeService } from '../../services/unsubscribe.service';
import { LoginService } from 'src/app/feature/login/services/login.service';
import { AppNotification } from '../../models/notification.interface';
import { NotificationsService } from '../../services/notifications.service';
import { StepperComponent } from '../stepper/stepper.component';
import { NotificationsPanelComponent } from '../notifications-panel/notifications-panel.component';
import { NotificationsModalComponent } from '../notifications-modal/notifications-modal.component';
import { LocationService } from '../../services/location.service';
import { UserValidationStateStore } from '../../state/user-validation-state.store';
import { UserCapabilitiesService } from '../../services/user-capabilities.service';
import {
  ProfileMenuActionId,
  ProfileMenuPanelComponent,
} from '../profile-menu-panel/profile-menu-panel.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    StepperComponent,
    NotificationsPanelComponent,
    NotificationsModalComponent,
    ProfileMenuPanelComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  isNotificationsPanelOpen = false;
  isProfileMenuPanelOpen = false;
  isNotificationsModalOpen = false;
  headerVariant: 'default' | 'onboarding' = 'default';
  hasNewNotifications$: Observable<boolean>;
  notifications$: Observable<AppNotification[]>;
  notificationsHasError$: Observable<boolean>;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly loadingService: LoadingService,
    private readonly unsubscribeService: UnsubscribeService,
    private readonly loginService: LoginService,
    private readonly notificationsService: NotificationsService,
    private readonly locationService: LocationService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly userValidationStateStore: UserValidationStateStore,
    private readonly capabilitiesService: UserCapabilitiesService,
  ) {
    this.hasNewNotifications$ = this.notificationsService.hasNew$;
    this.notifications$ = this.notificationsService.notifications$;
    this.notificationsHasError$ = this.notificationsService.hasError$;
  }

  ngOnInit() {
    const sessionTokenSubscription =
      this.sessionService.sessionToken$.subscribe((token) => {
        this.isLoggedIn = !!token;
        if (!this.isLoggedIn) {
          this.isNotificationsPanelOpen = false;
          this.isProfileMenuPanelOpen = false;
          this.isNotificationsModalOpen = false;
        }
      });
    this.subscriptions.add(sessionTokenSubscription);

    this.headerVariant = this.resolveHeaderVariant();
    const routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.headerVariant = this.resolveHeaderVariant();
      });
    this.subscriptions.add(routerSubscription);
  }

  onProfileMenuClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isNotificationsPanelOpen = false;
    this.isProfileMenuPanelOpen = !this.isProfileMenuPanelOpen;
  }

  handleProfileMenuAction(action: ProfileMenuActionId): void {
    this.isProfileMenuPanelOpen = false;

    switch (action) {
      case 'home':
        this.router.navigate(['/dashboard'], { replaceUrl: true });
        break;
      case 'convert':
        this.router.navigate(['/trading/coins-exchange'], { replaceUrl: true });
        break;
      case 'scheduledOrders':
        this.router.navigate(['/trading/scheduled-orders'], {
          replaceUrl: true,
        });
        break;
      case 'portfolio':
        this.router.navigate(['/dashboard/balance'], { replaceUrl: true });
        break;
      case 'accounts':
        this.router.navigate(['/dashboard/accounts']);
        break;
      case 'support':
        this.router.navigate(['/dashboard/support']);
        break;
      case 'terms':
        this.router.navigate(['/dashboard/terms']);
        break;
      case 'unsubscribe':
        this.unsubscribeService.show();
        break;
      case 'logout':
        this.performLogout();
        break;
    }
  }

  private performLogout(): void {
    this.loadingService.show();
    const logoutSubscription = this.loginService.logout().subscribe(() => {
      this.sessionService.setSessionToken(null);
      this.userValidationStateStore.clear();
      this.capabilitiesService.clear();
      this.isLoggedIn = false;
      this.loadingService.hide();
      this.router.navigate(['/login'], {
        replaceUrl: true,
      });
    });
    this.subscriptions.add(logoutSubscription);
  }

  /** Expuesto solo para compatibilidad con tests existentes (`menuSelected`) */
  menuSelected(event: string): void {
    const legacy: Record<string, ProfileMenuActionId> = {
      dashboard: 'home',
      unsubscribe: 'unsubscribe',
      logout: 'logout',
    };
    const action = legacy[event];
    if (action) this.handleProfileMenuAction(action);
  }

  onNotificationsClick(event: MouseEvent) {
    event.stopPropagation();
    this.isProfileMenuPanelOpen = false;
    this.isNotificationsPanelOpen = !this.isNotificationsPanelOpen;
  }

  onNotificationSelected(notification: AppNotification) {
    if (!notification.isRead) {
      const markSub = this.notificationsService
        .markAsRead(notification.id)
        .subscribe({
          error: () => {
            // Silenciar error: se reintentará con el siguiente load().
          },
        });
      this.subscriptions.add(markSub);
    }
  }

  onViewPreviousNotifications() {
    this.isNotificationsPanelOpen = false;
    this.isNotificationsModalOpen = true;
  }

  closeNotificationsModal() {
    this.isNotificationsModalOpen = false;
  }

  onMarkAllNotificationsAsRead() {
    const markAllSub = this.notificationsService.markAllAsRead().subscribe({
      error: () => {
        // Silenciar error: se reintentará con el siguiente load().
      },
    });
    this.subscriptions.add(markAllSub);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isNotificationsPanelOpen = false;
      this.isProfileMenuPanelOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isNotificationsPanelOpen = false;
    this.isProfileMenuPanelOpen = false;
  }

  goToHome() {
    if (this.isLoggedIn) {
      this.router.navigate(['/dashboard'], { replaceUrl: true });
    } else {
      this.locationService.reload();
    }
  }

  /** Logo del header en flujos onboarding: vuelve a la ruta raíz. */
  goToOnboardingRoot(): void {
    this.router.navigate(['/'], { replaceUrl: true });
  }

  onOnboardingLogoKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.goToOnboardingRoot();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private resolveHeaderVariant(): 'default' | 'onboarding' {
    let route = this.activatedRoute.root;
    let variant: 'default' | 'onboarding' = 'default';
    while (route.firstChild) {
      route = route.firstChild;
      const data = route.snapshot.data ?? {};
      if (data['headerVariant'] === 'onboarding') {
        variant = 'onboarding';
      }
    }
    return variant;
  }
}
